// src/services/azureBelegeService.js
// Azure Table Storage + Blob Storage Service für Zahlungsplan-Belege (PDF-Rechnungen
// von Subplaner:innen). Muster: azurePlansService.js (projektbezogene Tabelle,
// do/while-Paginierung, automatische Tabellen-/Container-Erstellung beim ersten Aufruf).
// Verknüpfung zur Zahlung: Budget-Einträge tragen ein optionales Feld `beleg_id`
// (siehe azureBudgetService.js) — ein Beleg kann mehrere Positionen abdecken.
//
// SICHERHEIT: nutzt wie alle azure*Services auf main den konto-weiten SAS-Token im
// Client (bekanntes zentrales Finding, siehe HANDOVER.md "Central security finding").
// Beim Portieren auf den Fork NICHT dieses Muster übernehmen, sondern auf dessen
// abgesicherte Zugriffsschicht umverdrahten. Container ist bewusst nicht öffentlich
// (Rechnungen sind vertraulich), Lesen nur über buildBelegFileUrl mit SAS.

const TABLE_CONFIG = {
  storageAccount: "mfprojects",
  belegeTable: "projectBelege",
  // Gleicher Account-SAS-Token wie azurePlansService.js / azurePendenzenService.js
  // (Table-Service, konto-weit gültig, daher auch für neue Tabellen nutzbar).
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const BLOB_CONFIG = {
  storageAccount: "mfprojects",
  containerName: "project-belege",
  // Gleicher Blob-SAS-Token wie azurePlansService.js (project-plans).
  sasToken:
    "sv=2024-11-04&ss=b&srt=co&sp=rwdlaciytfx&se=2035-10-02T19:07:10Z&st=2025-10-02T10:52:10Z&spr=https&sig=A8jWH2s8LkqmqiQjwjR8bL4sLjDKgKlE1HkDCZSlsYk%3D",
};

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB

const cleanValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const generateBelegId = () =>
  `beleg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Stellt sicher, dass die Tabelle existiert (Anlage beim ersten Aufruf).
let tableEnsured = false;
const ensureTableExists = async () => {
  if (tableEnsured) return true;
  try {
    const url = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/Tables?${TABLE_CONFIG.sasToken}`;
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify({ TableName: TABLE_CONFIG.belegeTable }),
    });
    tableEnsured = true;
    return true;
  } catch (error) {
    console.error("Belege: Tabelle konnte nicht angelegt werden:", error);
    return false;
  }
};

// Stellt sicher, dass der Blob-Container existiert (nicht öffentlich).
let containerEnsured = false;
const ensureContainerExists = async () => {
  if (containerEnsured) return true;
  try {
    const url = `https://${BLOB_CONFIG.storageAccount}.blob.core.windows.net/${BLOB_CONFIG.containerName}?restype=container&${BLOB_CONFIG.sasToken}`;
    const response = await fetch(url, { method: "PUT" });
    if (response.ok || response.status === 409) {
      containerEnsured = true;
    }
    return containerEnsured;
  } catch (error) {
    console.error("Belege: Container konnte nicht angelegt werden:", error);
    return false;
  }
};

// Lese-URL für einen Beleg (Container ist nicht öffentlich, daher mit SAS-Token).
// Wird für die Vorschau (iframe) in der Belege-Übersicht verwendet.
export const buildBelegFileUrl = (blobPath) =>
  `https://${BLOB_CONFIG.storageAccount}.blob.core.windows.net/${BLOB_CONFIG.containerName}/${blobPath}?${BLOB_CONFIG.sasToken}`;

// Datei validieren: nur PDF, max. 20 MB.
export const validateBelegFile = (file) => {
  if (!file) throw new Error("Keine Datei ausgewählt.");
  if (file.type !== "application/pdf") {
    throw new Error("Ungültiges Format. Für Belege ist nur PDF erlaubt.");
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(
      `Datei zu gross: ${(file.size / 1024 / 1024).toFixed(1)} MB. Maximal 20 MB.`
    );
  }
  return true;
};

const uploadBlob = async (blobPath, file) => {
  const url = `https://${BLOB_CONFIG.storageAccount}.blob.core.windows.net/${BLOB_CONFIG.containerName}/${blobPath}?${BLOB_CONFIG.sasToken}`;
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": file.type || "application/pdf",
    },
    body: file,
  });
  if (!response.ok) {
    const errorText = await response.text();
    console.error("Beleg-Blob-Upload-Fehler:", errorText);
    throw new Error("Der Beleg konnte nicht hochgeladen werden.");
  }
};

const deleteBlob = async (blobPath) => {
  const url = `https://${BLOB_CONFIG.storageAccount}.blob.core.windows.net/${BLOB_CONFIG.containerName}/${blobPath}?${BLOB_CONFIG.sasToken}`;
  const response = await fetch(url, { method: "DELETE" });
  if (!response.ok && response.status !== 404) {
    console.error("Beleg-Blob-Delete-Fehler:", await response.text());
  }
};

const mapBeleg = (item) => ({
  id: item.RowKey,
  projectCode: item.PartitionKey,
  fileName: item.FileName || "",
  blobPath: item.BlobPath || "",
  fileSize: parseInt(item.FileSize, 10) || 0,
  hochgeladenAm: item.HochgeladenAm || "",
  hochgeladenVon: item.HochgeladenVon || "",
});

// Alle Belege eines Projekts laden (neueste zuerst).
export const getBelege = async (projectCode) => {
  if (!projectCode) return [];
  try {
    await ensureTableExists();
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;
    do {
      let url = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${TABLE_CONFIG.belegeTable}()?${TABLE_CONFIG.sasToken}&$filter=${encodeURIComponent(
        `PartitionKey eq '${projectCode}'`
      )}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }
      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }
      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);
      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);
    return allEntries
      .map(mapBeleg)
      .sort((a, b) => (b.hochgeladenAm || "").localeCompare(a.hochgeladenAm || ""));
  } catch (error) {
    console.error("Fehler beim Laden der Belege:", error);
    throw new Error("Die Belege konnten nicht geladen werden.");
  }
};

// Beleg hochladen (Blob + Tabellenzeile) und gemappt zurückgeben.
export const uploadBeleg = async (projectCode, file, hochgeladenVon) => {
  try {
    if (!projectCode) throw new Error("Projektcode fehlt.");
    validateBelegFile(file);
    await ensureTableExists();
    await ensureContainerExists();

    const belegId = generateBelegId();
    const now = new Date().toISOString();
    const blobPath = `${projectCode}/${belegId}/original.pdf`;

    await uploadBlob(blobPath, file);

    const entity = {
      PartitionKey: String(projectCode),
      RowKey: belegId,
      FileName: cleanValue(file.name),
      BlobPath: blobPath,
      FileSize: file.size,
      HochgeladenAm: now,
      HochgeladenVon: cleanValue(hochgeladenVon),
    };

    const url = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${TABLE_CONFIG.belegeTable}?${TABLE_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });
    if (!response.ok) {
      // Tabellenzeile fehlgeschlagen: Blob wieder aufräumen, sonst bleibt eine Leiche.
      await deleteBlob(blobPath);
      throw new Error(`Azure Save Error: ${response.status}`);
    }

    return mapBeleg(entity);
  } catch (error) {
    console.error("Fehler beim Hochladen des Belegs:", error);
    throw new Error(error.message || "Der Beleg konnte nicht gespeichert werden.");
  }
};

// Beleg löschen (Blob + Tabellenzeile). Das Lösen der beleg_id-Verknüpfungen auf
// den Budget-Einträgen ist Aufrufer-Verantwortung (ZahlungsplanModal), damit dieser
// Service nicht vom Budget-Service abhängt.
export const deleteBeleg = async (beleg) => {
  try {
    if (beleg.blobPath) {
      await deleteBlob(beleg.blobPath);
    }
    const url = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${TABLE_CONFIG.belegeTable}(PartitionKey='${beleg.projectCode}',RowKey='${beleg.id}')?${TABLE_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Azure Delete Error: ${response.status}`);
    }
    return true;
  } catch (error) {
    console.error("Fehler beim Löschen des Belegs:", error);
    throw new Error("Der Beleg konnte nicht gelöscht werden.");
  }
};
